import { useState, useCallback, useMemo, createContext, useContext, ReactNode } from 'react';
import { roundMoney } from '../utils/money';
import {
  applyUniformDiscountToLines,
  applyUniformMarkupToLines,
  buildDefaultLinePricingFields,
  buildLinePricingFields,
  calculateAdjustedPrice,
  type LineAdjustmentType,
} from '../utils/sales/line-pricing.utils';
import { buildOrderSummary } from '../utils/sales/order-summary.utils';
import { createPaymentEntryId } from '../utils/sales/payment.utils';
import { SALES_CUSTOMER_STEP_ENABLED } from '../config/feature-flags';
import {
  SalesModalState,
  SalesStep2FlowPhase,
  SelectedProduct,
  DiscountConfig,
  MarkupConfig,
  PaymentConfig,
  PaymentEntry,
  OrderSummary,
  WarehouseInventoryProduct,
  CustomerProfile,
  Location,
  SelectedSeller,
  OrderChargeDraft,
  ChargeCategory,
} from '../types/sales.types';
import { isRemoteCartItem } from '../utils/sales/mixed-sale.utils';
import { useChargeCategories } from './useChargeCategories';

const initialDiscountConfig: DiscountConfig = {
  enabled: false,
  scope: 'PER_ITEM',
  allowIndividualDiscounts: false,
  orderDiscountType: 'AMOUNT',
  orderDiscountValue: 0,
};

const initialMarkupConfig: MarkupConfig = {
  enabled: false,
  scope: 'PER_ITEM',
  allowIndividualMarkups: false,
  orderMarkupType: 'AMOUNT',
  orderMarkupValue: 0,
};

const initialPaymentConfig: PaymentConfig = {
  allowPartialPayment: false,
  partialPaymentScope: 'PER_ORDER',
  payments: [],
  allowMultiplePayments: false,
  useGlobalPayment: true,
  cashAmount: 0,
  partialPaymentAmount: 0,
};

const initialSalesModalState: SalesModalState = {
  currentStep: 1,
  step2FlowPhase: 1,
  step2PaymentsLocked: false,
  selectedLocation: null,
  selectedProducts: [],
  selectedCustomer: null,
  selectedSeller: null,
  saleDate: new Date(),
  discountConfig: initialDiscountConfig,
  markupConfig: initialMarkupConfig,
  paymentConfig: initialPaymentConfig,
  orderCharges: [],
  hasInvoice: false,
  orderSummary: null,
};

function clearPaymentEntries(paymentConfig: PaymentConfig): PaymentConfig {
  return {
    ...paymentConfig,
    payments: [],
    globalPaymentMethod: undefined,
  };
}

function resetSelectedProductPricing(products: SelectedProduct[]): SelectedProduct[] {
  return products.map((product) => ({
    ...product,
    ...buildDefaultLinePricingFields(product.originalPrice),
  }));
}

const MAX_STEPS = SALES_CUSTOMER_STEP_ENABLED ? 4 : 3;

function mapLineFields(
  products: SelectedProduct[],
  mapper: (product: SelectedProduct) => Partial<SelectedProduct>,
): SelectedProduct[] {
  return products.map((product) => ({
    ...product,
    ...mapper(product),
  }));
}

function toDistributionLines(products: SelectedProduct[]) {
  return products.map((product) => ({
    id: product.warehouseInventoryProduct.id,
    originalPrice: product.originalPrice,
    markupType: product.markupType,
    markupValue: product.markupValue,
  }));
}

function shouldApplyUniformDiscount(
  discountConfig: DiscountConfig,
): discountConfig is DiscountConfig & { orderDiscountValue: number } {
  return (
    discountConfig.enabled &&
    !discountConfig.allowIndividualDiscounts &&
    discountConfig.orderDiscountValue > 0
  );
}

function shouldApplyUniformMarkup(
  markupConfig: MarkupConfig,
): markupConfig is MarkupConfig & { orderMarkupValue: number } {
  return (
    markupConfig.enabled &&
    !markupConfig.allowIndividualMarkups &&
    markupConfig.orderMarkupValue > 0
  );
}

function applyUniformMarkupIfNeeded(
  products: SelectedProduct[],
  markupConfig: MarkupConfig,
): SelectedProduct[] {
  if (!shouldApplyUniformMarkup(markupConfig)) {
    return products;
  }

  const markupFieldsByLineId = applyUniformMarkupToLines(
    products.map((product) => ({
      id: product.warehouseInventoryProduct.id,
      originalPrice: product.originalPrice,
    })),
    {
      markupType: markupConfig.orderMarkupType,
      markupValue: markupConfig.orderMarkupValue,
    },
  );

  return products.map((product) => {
    const markupFields = markupFieldsByLineId.get(product.warehouseInventoryProduct.id);
    if (markupFields == null) {
      return product;
    }

    const pricing = buildLinePricingFields(
      product.originalPrice,
      markupFields.markupType,
      markupFields.markupValue,
      'NONE',
      0,
    );

    return {
      ...product,
      ...pricing,
    };
  });
}

function syncGlobalPaymentMethodFromPayments(
  pc: PaymentConfig,
  payments: PaymentEntry[],
): Partial<PaymentConfig> {
  if (!pc.useGlobalPayment) {
    return {};
  }
  if (!pc.allowMultiplePayments) {
    return payments.length === 1
      ? { globalPaymentMethod: payments[0].method }
      : { globalPaymentMethod: undefined };
  }
  if (payments.length === 1) {
    return { globalPaymentMethod: payments[0].method };
  }
  return { globalPaymentMethod: undefined };
}

interface SalesModalContextType {
  state: SalesModalState;
  isOpen: boolean;
  isLoading: boolean;
  openModal: () => void;
  closeModal: () => void;
  finish: () => void;
  goToStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  setSelectedLocation: (location: Location) => void;
  setSelectedSeller: (seller: SelectedSeller | null) => void;
  addProduct: (warehouseInventoryProduct: WarehouseInventoryProduct) => void;
  removeProduct: (productId: number) => void;
  clearCart: () => void;
  updateProductMarkup: (
    productId: number,
    markupType: LineAdjustmentType,
    markupValue: number,
  ) => void;
  updateProductDiscount: (
    productId: number,
    discountType: LineAdjustmentType,
    discountValue: number,
  ) => void;
  applyUniformMarkupToAllProducts: (
    markupType: 'PERCENTAGE' | 'AMOUNT',
    markupValue: number,
  ) => void;
  resetAllProductMarkups: () => void;
  updateProductPaymentMethod: (productId: number, paymentMethod: 'CASH' | 'CARD' | 'QR') => void;
  setSelectedCustomer: (customer: CustomerProfile | null) => void;
  setSaleDate: (date: Date) => void;
  updateDiscountConfig: (config: Partial<DiscountConfig>) => void;
  updateMarkupConfig: (config: Partial<MarkupConfig>) => void;
  applyUniformDiscountToAllProducts: (
    discountType: 'PERCENTAGE' | 'AMOUNT',
    discountValue: number,
  ) => void;
  resetAllProductDiscounts: () => void;
  resetAllLinePricing: () => void;
  commitCommercialPricingConfig: (draft: {
    markupEnabled: boolean;
    allowIndividualMarkups: boolean;
    markupType: 'PERCENTAGE' | 'AMOUNT';
    markupValue: number;
    discountEnabled: boolean;
    allowIndividualDiscounts: boolean;
    discountType: 'PERCENTAGE' | 'AMOUNT';
    discountValue: number;
  }) => void;
  setStep2FlowPhase: (phase: SalesStep2FlowPhase) => void;
  setStep2PaymentsLocked: (locked: boolean) => void;
  clearPayments: () => void;
  updatePaymentConfig: (config: Partial<PaymentConfig>) => void;
  addPayment: (payment: Omit<PaymentEntry, 'id'>) => string;
  removePayment: (paymentId: string) => void;
  updatePayment: (paymentId: string, updates: Partial<PaymentEntry>) => void;
  addOrderCharge: (charge: OrderChargeDraft) => void;
  updateOrderCharge: (chargeId: string, updates: Partial<OrderChargeDraft>) => void;
  removeOrderCharge: (chargeId: string) => void;
  setHasInvoice: (hasInvoice: boolean) => void;
  setProductDelivered: (productId: number, isDelivered: boolean) => void;
  applyDeliveryDefaults: (
    paymentStatus: OrderSummary['paymentStatus'],
    sellingLocationId: number | undefined,
  ) => void;
  chargeCategories: ChargeCategory[];
  calculateOrderSummary: OrderSummary;
  orderSummary: OrderSummary;
  canProceedToStep2: boolean;
  canProceedToStep3: boolean;
  setLoading: (loading: boolean) => void;
  setOnOrderCreated: (callback: () => void) => void;
}

const SalesModalContext = createContext<SalesModalContextType | undefined>(undefined);

export const SalesModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<SalesModalState>(initialSalesModalState);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [onOrderCreated, setOnOrderCreated] = useState<(() => void)[]>([]);

  // Fetched once per modal session (not per step) so Step2 and Step3 share the same list.
  const { categories: chargeCategories } = useChargeCategories(isOpen);

  const resetState = useCallback(() => {
    setState({
      ...initialSalesModalState,
      saleDate: new Date(),
      paymentConfig: {
        ...initialPaymentConfig,
        payments: [],
        globalPaymentMethod: undefined,
      },
    });
    setOnOrderCreated([]);
  }, []);

  const openModal = useCallback(() => {
    setState((prev) => ({ ...prev, saleDate: new Date() }));
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    resetState();
  }, [resetState]);

  const finish = useCallback(() => {
    if (onOrderCreated.length > 0) {
      onOrderCreated.forEach((callback) => callback());
    }
    closeModal();
  }, [onOrderCreated, closeModal]);

  const goToStep = useCallback((step: number) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  }, []);

  const nextStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, MAX_STEPS),
    }));
  }, []);

  const previousStep = useCallback(() => {
    setState((prev) => {
      const nextStep = Math.max(prev.currentStep - 1, 1);

      if (nextStep === 2 && prev.currentStep > 2) {
        return {
          ...prev,
          currentStep: nextStep,
          step2FlowPhase: 5,
          step2PaymentsLocked: true,
        };
      }

      if (prev.currentStep === 2 && nextStep === 1) {
        return {
          ...prev,
          currentStep: nextStep,
          step2FlowPhase: 1,
          step2PaymentsLocked: false,
          discountConfig: initialDiscountConfig,
          markupConfig: initialMarkupConfig,
          paymentConfig: clearPaymentEntries(prev.paymentConfig),
          selectedProducts: resetSelectedProductPricing(prev.selectedProducts),
        };
      }

      return { ...prev, currentStep: nextStep };
    });
  }, []);

  const setStep2FlowPhase = useCallback((phase: SalesStep2FlowPhase) => {
    setState((prev) => ({ ...prev, step2FlowPhase: phase }));
  }, []);

  const setStep2PaymentsLocked = useCallback((locked: boolean) => {
    setState((prev) => ({ ...prev, step2PaymentsLocked: locked }));
  }, []);

  const clearPayments = useCallback(() => {
    setState((prev) => ({
      ...prev,
      paymentConfig: clearPaymentEntries(prev.paymentConfig),
    }));
  }, []);

  const resetAllLinePricing = useCallback(() => {
    setState((prev) => ({
      ...prev,
      discountConfig: initialDiscountConfig,
      markupConfig: initialMarkupConfig,
      selectedProducts: resetSelectedProductPricing(prev.selectedProducts),
    }));
  }, []);

  /**
   * Single atomic pricing transaction: reset → configs → uniform markup/discount.
   * Advances to phase 2 when commercial config is active; otherwise skips to charges (phase 3).
   */
  const commitCommercialPricingConfig = useCallback(
    (draft: {
      markupEnabled: boolean;
      allowIndividualMarkups: boolean;
      markupType: 'PERCENTAGE' | 'AMOUNT';
      markupValue: number;
      discountEnabled: boolean;
      allowIndividualDiscounts: boolean;
      discountType: 'PERCENTAGE' | 'AMOUNT';
      discountValue: number;
    }) => {
      setState((prev) => {
        let selectedProducts = resetSelectedProductPricing(prev.selectedProducts);
        const hasCommercialConfig = draft.markupEnabled || draft.discountEnabled;

        const markupConfig: MarkupConfig = {
          enabled: draft.markupEnabled,
          allowIndividualMarkups: draft.allowIndividualMarkups,
          orderMarkupType: draft.markupType,
          orderMarkupValue: roundMoney(Math.max(0, draft.markupValue)),
          scope: draft.markupType === 'AMOUNT' ? 'PER_ORDER' : 'PER_ITEM',
        };

        const discountConfig: DiscountConfig = {
          enabled: draft.discountEnabled,
          allowIndividualDiscounts: draft.allowIndividualDiscounts,
          orderDiscountType: draft.discountType,
          orderDiscountValue: roundMoney(Math.max(0, draft.discountValue)),
          scope: draft.discountType === 'AMOUNT' ? 'PER_ORDER' : 'PER_ITEM',
        };

        if (
          markupConfig.enabled &&
          !markupConfig.allowIndividualMarkups &&
          markupConfig.orderMarkupValue > 0
        ) {
          const markupFieldsByLineId = applyUniformMarkupToLines(
            toDistributionLines(selectedProducts),
            {
              markupType: markupConfig.orderMarkupType,
              markupValue: markupConfig.orderMarkupValue,
            },
          );
          selectedProducts = selectedProducts.map((product) => {
            const markupFields = markupFieldsByLineId.get(product.warehouseInventoryProduct.id);
            if (markupFields == null) return product;
            return {
              ...product,
              ...buildLinePricingFields(
                product.originalPrice,
                markupFields.markupType,
                markupFields.markupValue,
                'NONE',
                0,
              ),
            };
          });
        }

        if (
          discountConfig.enabled &&
          !discountConfig.allowIndividualDiscounts &&
          discountConfig.orderDiscountValue > 0
        ) {
          const discountFieldsByLineId = applyUniformDiscountToLines(
            toDistributionLines(selectedProducts),
            {
              discountType: discountConfig.orderDiscountType,
              discountValue: discountConfig.orderDiscountValue,
            },
          );
          selectedProducts = selectedProducts.map((product) => {
            const discountFields = discountFieldsByLineId.get(product.warehouseInventoryProduct.id);
            if (discountFields == null) return product;
            const adjustedPrice = calculateAdjustedPrice(
              product.originalPrice,
              product.markupType,
              product.markupValue,
            );
            return {
              ...product,
              adjustedPrice,
              ...discountFields,
            };
          });
        }

        return {
          ...prev,
          markupConfig,
          discountConfig,
          selectedProducts,
          step2FlowPhase: hasCommercialConfig ? 2 : 3,
          paymentConfig: clearPaymentEntries(prev.paymentConfig),
          step2PaymentsLocked: false,
        };
      });
    },
    [],
  );

  const setSelectedLocation = useCallback((location: Location) => {
    setState((prev) => ({ ...prev, selectedLocation: location }));
  }, []);

  const setSelectedSeller = useCallback((seller: SelectedSeller | null) => {
    setState((prev) => ({ ...prev, selectedSeller: seller }));
  }, []);

  const applyUniformDiscountIfNeeded = useCallback(
    (products: SelectedProduct[], discountConfig: DiscountConfig) => {
      if (!shouldApplyUniformDiscount(discountConfig)) {
        return products;
      }

      const discountFieldsByLineId = applyUniformDiscountToLines(toDistributionLines(products), {
        discountType: discountConfig.orderDiscountType,
        discountValue: discountConfig.orderDiscountValue,
      });

      return products.map((product) => {
        const discountFields = discountFieldsByLineId.get(product.warehouseInventoryProduct.id);
        if (discountFields == null) {
          return product;
        }
        const adjustedPrice = calculateAdjustedPrice(
          product.originalPrice,
          product.markupType,
          product.markupValue,
        );
        return {
          ...product,
          adjustedPrice,
          ...discountFields,
        };
      });
    },
    [],
  );

  const addProduct = useCallback(
    (warehouseInventoryProduct: WarehouseInventoryProduct) => {
      setState((prev) => {
        const existingProduct = prev.selectedProducts.find(
          (p) => p.warehouseInventoryProduct.id === warehouseInventoryProduct.id,
        );

        if (existingProduct != null) {
          return prev;
        }

        const originalPrice = roundMoney(
          Number(
            warehouseInventoryProduct.salePrice ?? warehouseInventoryProduct.product.salePrice,
          ) || 0,
        );

        const newProduct: SelectedProduct = {
          warehouseInventoryProduct,
          originalPrice,
          ...buildDefaultLinePricingFields(originalPrice),
          isDelivered: false,
        };

        let selectedProducts = [...prev.selectedProducts, newProduct];
        selectedProducts = applyUniformMarkupIfNeeded(selectedProducts, prev.markupConfig);
        selectedProducts = applyUniformDiscountIfNeeded(selectedProducts, prev.discountConfig);

        return {
          ...prev,
          selectedProducts,
        };
      });
    },
    [applyUniformDiscountIfNeeded],
  );

  const removeProduct = useCallback(
    (productId: number) => {
      setState((prev) => {
        let selectedProducts = prev.selectedProducts.filter(
          (p) => p.warehouseInventoryProduct.id !== productId,
        );
        selectedProducts = applyUniformMarkupIfNeeded(selectedProducts, prev.markupConfig);
        selectedProducts = applyUniformDiscountIfNeeded(selectedProducts, prev.discountConfig);
        return { ...prev, selectedProducts };
      });
    },
    [applyUniformDiscountIfNeeded],
  );

  const clearCart = useCallback(() => {
    setState((prev) => ({ ...prev, selectedProducts: [] }));
  }, []);

  const updateProductMarkup = useCallback(
    (productId: number, markupType: LineAdjustmentType, markupValue: number) => {
      setState((prev) => ({
        ...prev,
        selectedProducts: prev.selectedProducts.map((product) => {
          if (product.warehouseInventoryProduct.id !== productId) {
            return product;
          }

          const pricing = buildLinePricingFields(
            product.originalPrice,
            markupType,
            markupValue,
            product.discountType,
            product.discountValue,
          );

          return {
            ...product,
            ...pricing,
          };
        }),
      }));
    },
    [],
  );

  const updateProductDiscount = useCallback(
    (productId: number, discountType: LineAdjustmentType, discountValue: number) => {
      setState((prev) => ({
        ...prev,
        selectedProducts: prev.selectedProducts.map((product) => {
          if (product.warehouseInventoryProduct.id !== productId) {
            return product;
          }

          const pricing = buildLinePricingFields(
            product.originalPrice,
            product.markupType,
            product.markupValue,
            discountType,
            discountValue,
          );

          return {
            ...product,
            ...pricing,
          };
        }),
      }));
    },
    [],
  );

  const applyUniformMarkupToAllProducts = useCallback(
    (markupType: 'PERCENTAGE' | 'AMOUNT', markupValue: number) => {
      setState((prev) => {
        const normalizedMarkupValue = roundMoney(Math.max(0, markupValue));
        const markupFieldsByLineId = applyUniformMarkupToLines(
          toDistributionLines(prev.selectedProducts),
          {
            markupType,
            markupValue: normalizedMarkupValue,
          },
        );

        let selectedProducts = prev.selectedProducts.map((product) => {
          const markupFields = markupFieldsByLineId.get(product.warehouseInventoryProduct.id);
          if (markupFields == null) {
            return product;
          }

          const pricing = buildLinePricingFields(
            product.originalPrice,
            markupFields.markupType,
            markupFields.markupValue,
            'NONE',
            0,
          );

          return {
            ...product,
            ...pricing,
          };
        });

        selectedProducts = applyUniformDiscountIfNeeded(selectedProducts, prev.discountConfig);

        return {
          ...prev,
          markupConfig: {
            ...prev.markupConfig,
            orderMarkupType: markupType,
            orderMarkupValue: normalizedMarkupValue,
            scope: markupType === 'AMOUNT' ? 'PER_ORDER' : 'PER_ITEM',
          },
          selectedProducts,
        };
      });
    },
    [applyUniformDiscountIfNeeded],
  );

  const resetAllProductMarkups = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedProducts: mapLineFields(prev.selectedProducts, (product) => {
        const pricing = buildLinePricingFields(
          product.originalPrice,
          'NONE',
          0,
          product.discountType,
          product.discountValue,
        );
        return pricing;
      }),
    }));
  }, []);

  const applyUniformDiscountToAllProducts = useCallback(
    (discountType: 'PERCENTAGE' | 'AMOUNT', discountValue: number) => {
      setState((prev) => {
        const settings = {
          discountType,
          discountValue: roundMoney(Math.max(0, discountValue)),
        };

        const discountFieldsByLineId = applyUniformDiscountToLines(
          toDistributionLines(prev.selectedProducts),
          settings,
        );

        return {
          ...prev,
          discountConfig: {
            ...prev.discountConfig,
            orderDiscountType: discountType,
            orderDiscountValue: settings.discountValue,
            scope: discountType === 'AMOUNT' ? 'PER_ORDER' : 'PER_ITEM',
          },
          selectedProducts: prev.selectedProducts.map((product) => {
            const discountFields = discountFieldsByLineId.get(product.warehouseInventoryProduct.id);
            if (discountFields == null) {
              return product;
            }
            const adjustedPrice = calculateAdjustedPrice(
              product.originalPrice,
              product.markupType,
              product.markupValue,
            );
            return {
              ...product,
              adjustedPrice,
              ...discountFields,
            };
          }),
        };
      });
    },
    [],
  );

  const resetAllProductDiscounts = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedProducts: mapLineFields(prev.selectedProducts, (product) => {
        return buildLinePricingFields(
          product.originalPrice,
          product.markupType,
          product.markupValue,
          'NONE',
          0,
        );
      }),
    }));
  }, []);

  const updateProductPaymentMethod = useCallback(
    (productId: number, paymentMethod: 'CASH' | 'CARD' | 'QR') => {
      setState((prev) => ({
        ...prev,
        selectedProducts: prev.selectedProducts.map((p) =>
          p.warehouseInventoryProduct.id === productId ? { ...p, paymentMethod } : p,
        ),
      }));
    },
    [],
  );

  const setSelectedCustomer = useCallback((customer: CustomerProfile | null) => {
    setState((prev) => ({ ...prev, selectedCustomer: customer }));
  }, []);

  const setSaleDate = useCallback((date: Date) => {
    setState((prev) => ({ ...prev, saleDate: date }));
  }, []);

  const updateDiscountConfig = useCallback((config: Partial<DiscountConfig>) => {
    setState((prev) => ({
      ...prev,
      discountConfig: { ...prev.discountConfig, ...config },
    }));
  }, []);

  const updateMarkupConfig = useCallback((config: Partial<MarkupConfig>) => {
    setState((prev) => ({
      ...prev,
      markupConfig: { ...prev.markupConfig, ...config },
    }));
  }, []);

  const updatePaymentConfig = useCallback((config: Partial<PaymentConfig>) => {
    setState((prev) => ({
      ...prev,
      paymentConfig: { ...prev.paymentConfig, ...config },
    }));
  }, []);

  const addPayment = useCallback((payment: Omit<PaymentEntry, 'id'>): string => {
    const id = createPaymentEntryId();
    const newPayment: PaymentEntry = {
      ...payment,
      id,
      amount: roundMoney(payment.amount),
      cashReceived: payment.cashReceived != null ? roundMoney(payment.cashReceived) : undefined,
    };

    setState((prev) => {
      const payments = prev.paymentConfig.allowMultiplePayments
        ? [...prev.paymentConfig.payments, newPayment]
        : [newPayment];
      return {
        ...prev,
        paymentConfig: {
          ...prev.paymentConfig,
          payments,
          ...syncGlobalPaymentMethodFromPayments(prev.paymentConfig, payments),
        },
      };
    });

    return id;
  }, []);

  const removePayment = useCallback((paymentId: string) => {
    setState((prev) => {
      const payments = prev.paymentConfig.payments.filter((p) => p.id !== paymentId);
      return {
        ...prev,
        paymentConfig: {
          ...prev.paymentConfig,
          payments,
          ...syncGlobalPaymentMethodFromPayments(prev.paymentConfig, payments),
        },
      };
    });
  }, []);

  const updatePayment = useCallback((paymentId: string, updates: Partial<PaymentEntry>) => {
    setState((prev) => {
      const payments = prev.paymentConfig.payments.map((p) => {
        if (p.id === paymentId) {
          const updated = { ...p, ...updates };
          if (updated.amount != null) {
            updated.amount = roundMoney(updated.amount);
          }
          if (updated.cashReceived != null) {
            updated.cashReceived = roundMoney(updated.cashReceived);
          }
          return updated;
        }
        return p;
      });
      return {
        ...prev,
        paymentConfig: {
          ...prev.paymentConfig,
          payments,
          ...syncGlobalPaymentMethodFromPayments(prev.paymentConfig, payments),
        },
      };
    });
  }, []);

  const calculateOrderSummary = useMemo((): OrderSummary => {
    return buildOrderSummary({
      lines: state.selectedProducts,
      charges: state.orderCharges,
      payments: state.paymentConfig.payments,
      allowPartialPayment: state.paymentConfig.allowPartialPayment,
    });
  }, [state.selectedProducts, state.paymentConfig, state.orderCharges]);

  const canProceedToStep2 = useMemo(() => {
    return Boolean(state.selectedLocation && state.selectedProducts.length > 0);
  }, [state.selectedLocation, state.selectedProducts]);

  const canProceedToStep3 = useMemo(() => {
    if (!state.selectedLocation || state.selectedProducts.length === 0) {
      return false;
    }

    const { payments, allowPartialPayment } = state.paymentConfig;
    const grandTotal = calculateOrderSummary.grandTotal;

    // Free order (catalog 0 / fully discounted): no payment required.
    if (grandTotal <= 0) {
      return true;
    }

    if (payments.length === 0) {
      return false;
    }

    const allPaymentsValid = payments.every((p) => p.amount > 0 && p.method);
    if (!allPaymentsValid) {
      return false;
    }

    const cashPayments = payments.filter((p) => p.method === 'CASH');
    for (const payment of cashPayments) {
      if (payment.cashReceived != null && payment.cashReceived < payment.amount) {
        return false;
      }
    }

    if (!allowPartialPayment) {
      const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);
      if (totalPayments < grandTotal) {
        return false;
      }
    }

    return true;
  }, [
    state.selectedLocation,
    state.selectedProducts.length,
    state.paymentConfig.payments,
    state.paymentConfig.allowPartialPayment,
    calculateOrderSummary.grandTotal,
  ]);

  const setOnOrderCreatedCallback = useCallback((callback: () => void) => {
    setOnOrderCreated((prev) => [...prev, callback]);
  }, []);

  const addOrderCharge = useCallback((charge: OrderChargeDraft) => {
    setState((prev) => ({
      ...prev,
      orderCharges: [...prev.orderCharges, charge],
    }));
  }, []);

  const updateOrderCharge = useCallback((chargeId: string, updates: Partial<OrderChargeDraft>) => {
    setState((prev) => ({
      ...prev,
      orderCharges: prev.orderCharges.map((charge) =>
        charge.id === chargeId ? { ...charge, ...updates, id: charge.id } : charge,
      ),
    }));
  }, []);

  const removeOrderCharge = useCallback((chargeId: string) => {
    setState((prev) => ({
      ...prev,
      orderCharges: prev.orderCharges.filter((charge) => charge.id !== chargeId),
    }));
  }, []);

  const setHasInvoice = useCallback((hasInvoice: boolean) => {
    setState((prev) => ({ ...prev, hasInvoice }));
  }, []);

  const setProductDelivered = useCallback((productId: number, isDelivered: boolean) => {
    setState((prev) => ({
      ...prev,
      selectedProducts: prev.selectedProducts.map((product) =>
        product.warehouseInventoryProduct.id === productId ? { ...product, isDelivered } : product,
      ),
    }));
  }, []);

  const applyDeliveryDefaults = useCallback(
    (paymentStatus: OrderSummary['paymentStatus'], sellingLocationId: number | undefined) => {
      setState((prev) => ({
        ...prev,
        selectedProducts: prev.selectedProducts.map((product) => {
          const isRemote = isRemoteCartItem(product, sellingLocationId);
          if (isRemote) {
            return { ...product, isDelivered: false };
          }
          return {
            ...product,
            isDelivered: paymentStatus === 'complete',
          };
        }),
      }));
    },
    [],
  );

  const contextValue: SalesModalContextType = {
    state,
    isOpen,
    isLoading,
    openModal,
    closeModal,
    finish,
    goToStep,
    nextStep,
    previousStep,
    setSelectedLocation,
    setSelectedSeller,
    addProduct,
    removeProduct,
    clearCart,
    updateProductMarkup,
    updateProductDiscount,
    applyUniformMarkupToAllProducts,
    resetAllProductMarkups,
    updateProductPaymentMethod,
    setSelectedCustomer,
    setSaleDate,
    updateDiscountConfig,
    updateMarkupConfig,
    applyUniformDiscountToAllProducts,
    resetAllProductDiscounts,
    resetAllLinePricing,
    commitCommercialPricingConfig,
    setStep2FlowPhase,
    setStep2PaymentsLocked,
    clearPayments,
    updatePaymentConfig,
    addPayment,
    removePayment,
    updatePayment,
    addOrderCharge,
    updateOrderCharge,
    removeOrderCharge,
    setHasInvoice,
    setProductDelivered,
    applyDeliveryDefaults,
    chargeCategories,
    calculateOrderSummary,
    orderSummary: calculateOrderSummary,
    canProceedToStep2,
    canProceedToStep3,
    setLoading: setIsLoading,
    setOnOrderCreated: setOnOrderCreatedCallback,
  };

  return <SalesModalContext.Provider value={contextValue}>{children}</SalesModalContext.Provider>;
};

export const useSalesModal = () => {
  const context = useContext(SalesModalContext);
  if (context === undefined) {
    throw new Error('useSalesModal must be used within a SalesModalProvider');
  }
  return context;
};
